import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, RefreshCw, Loader2, ScrollText, Gavel, Clock, AlertTriangle, ExternalLink, ArrowUpRight, CheckCircle2, X, Radar, BellRing, Ban, GitCompareArrows } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/datetime";

const STATUS_META = {
  cited_statute: { label: "Cited statute", cls: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" },
  reference_source: { label: "Reference source", cls: "text-sky-300 border-sky-500/40 bg-sky-500/10" },
  no_citation: { label: "No citation", cls: "text-rose-300 border-rose-500/40 bg-rose-500/10" },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.no_citation;
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded-sm border text-[10px] font-mono whitespace-nowrap ${m.cls}`}>
      {m.label}
    </span>
  );
}

function StaleFlag({ staleness }) {
  if (!staleness?.is_stale) {
    return <span className="text-emerald-400 text-[10px] font-mono">current</span>;
  }
  const label = staleness.reason === "never_reviewed" ? "Never reviewed" : "Review overdue";
  return (
    <span className="inline-flex items-center gap-1 text-amber-300 text-[10px] font-mono" title={staleness.reason}>
      <AlertTriangle className="h-3 w-3" /> {label}
    </span>
  );
}

function SourcingTable({ title, icon: Icon, data, testid, system, onReview }) {
  const s = data?.summary || {};
  return (
    <div className="ae-card p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-[10px] uppercase tracking-widest text-emerald-400 inline-flex items-center gap-1.5">
          <Icon className="h-3 w-3" /> {title}
        </div>
        <div className="text-[10px] text-slate-500 font-mono" data-testid={`${testid}-summary`}>
          {s.total} states · <span className="text-amber-300">{s.stale} stale</span> ·{" "}
          {s.by_citation_status?.cited_statute || 0} cited /{" "}
          {s.by_citation_status?.reference_source || 0} ref /{" "}
          {s.by_citation_status?.no_citation || 0} none
        </div>
      </div>
      <div className="overflow-auto max-h-[720px] border border-white/8 rounded-sm">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-[#0A0D12]">
            <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-white/8">
              <th className="text-left px-2 py-2">State</th>
              <th className="text-left px-2 py-2">Citation status</th>
              <th className="text-left px-2 py-2">Citation / detail</th>
              <th className="text-left px-2 py-2">Last reviewed</th>
              <th className="text-left px-2 py-2">Staleness</th>
              <th className="text-left px-2 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows || []).map((r) => (
              <tr key={r.state} className="border-b border-white/5 align-top" data-testid={`${testid}-row-${r.state}`}>
                <td className="px-2 py-2 whitespace-nowrap">
                  <span className="font-mono text-slate-200">{r.state}</span>
                  <div className="text-[9px] text-slate-500">{r.state_name}</div>
                  {r.under_review && (
                    <span className="text-[9px] text-amber-400 font-mono">under review</span>
                  )}
                </td>
                <td className="px-2 py-2"><StatusBadge status={r.citation_status} /></td>
                <td className="px-2 py-2 text-slate-300 max-w-[560px]">
                  {r.citation_text ? (
                    system === "contact_hours" && r.citation_url ? (
                      <a
                        href={r.citation_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`${testid}-citation-link-${r.state}`}
                        className="font-mono text-[10px] text-emerald-300 hover:text-emerald-200 underline decoration-dotted inline-flex items-start gap-1"
                        title={`Open primary source: ${r.citation_url}`}
                      >
                        {r.citation_text}
                        <ExternalLink className="h-2.5 w-2.5 mt-0.5 shrink-0" />
                      </a>
                    ) : (
                      <span className="font-mono text-[10px] text-slate-300">{r.citation_text}</span>
                    )
                  ) : (
                    <span className="text-slate-600 italic">, </span>
                  )}
                  <div className="text-[9px] text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                    <span>{r.detail}</span>
                    {system === "document_library" && (
                      <>
                        <Link
                          to={`/app/documents/library?state=${r.state}`}
                          data-testid={`${testid}-deeplink-${r.state}`}
                          className="text-sky-400 hover:text-sky-300 inline-flex items-center gap-0.5"
                          title="Open this state on the State Requirements page"
                        >
                          State Requirements <ArrowUpRight className="h-2.5 w-2.5" />
                        </Link>
                        {r.citation_url ? (
                          <a
                            href={r.citation_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`${testid}-source-link-${r.state}`}
                            className="text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-0.5"
                            title={`Verified primary source: ${r.citation_url}`}
                          >
                            Verified source <ExternalLink className="h-2.5 w-2.5" />
                          </a>
                        ) : r.citation_text ? (
                          <a
                            href={`https://www.google.com/search?q=${encodeURIComponent(`${r.citation_text} ${r.state_name || ""} statute`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`${testid}-source-link-${r.state}`}
                            className="text-slate-400 hover:text-slate-200 inline-flex items-center gap-0.5 underline decoration-dotted"
                            title="Web search for this citation ,  no verified direct URL on file yet"
                          >
                            Research source <ExternalLink className="h-2.5 w-2.5" />
                          </a>
                        ) : null}
                      </>
                    )}
                  </div>
                </td>
                <td className="px-2 py-2 text-slate-500 font-mono text-[10px] whitespace-nowrap">
                  {r.last_attorney_reviewed_at ? (
                    <span title={r.reviewed_by ? `Reviewed by ${r.reviewed_by}` : undefined}>
                      {fmtDateTime(r.last_attorney_reviewed_at)}
                      {r.reviewed_by && <div className="text-[9px] text-slate-600">by {r.reviewed_by}</div>}
                    </span>
                  ) : ", "}
                </td>
                <td className="px-2 py-2">
                  <StaleFlag staleness={r.staleness} />
                  {r.has_pending_proposal && (
                    <div
                      data-testid={`${testid}-update-badge-${r.state}`}
                      className="mt-1 inline-flex items-center gap-1 text-[9px] font-mono text-amber-300 border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 rounded-sm"
                      title="A source change was detected ,  see Pending Source Updates below"
                    >
                      <BellRing className="h-2.5 w-2.5" /> Update available
                    </div>
                  )}
                  {r.source_check_status === "failed" && (
                    <div
                      data-testid={`${testid}-checkfail-badge-${r.state}`}
                      className="mt-1 inline-flex items-center gap-1 text-[9px] font-mono text-rose-300 border border-rose-500/40 bg-rose-500/10 px-1.5 py-0.5 rounded-sm"
                      title={`Source could not be auto-fetched (${r.source_check_detail || "check failed"}) ,  needs manual review`}
                    >
                      <Ban className="h-2.5 w-2.5" /> Check failed
                    </div>
                  )}
                </td>
                <td className="px-2 py-2">
                  <button
                    onClick={() => onReview({ ...r, system })}
                    data-testid={`${testid}-review-btn-${r.state}`}
                    className="text-[10px] px-2 py-1 rounded-sm border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 inline-flex items-center gap-1 whitespace-nowrap"
                    title="Record a review (clears staleness)"
                  >
                    <CheckCircle2 className="h-3 w-3" /> Mark reviewed
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReviewModal({ row, onClose, onSubmit }) {
  const [reviewer, setReviewer] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!row) return null;

  const valid = reviewer.trim().length > 0 && note.trim().length >= 5;

  const submit = async () => {
    if (!valid) return;
    setSubmitting(true);
    try {
      await onSubmit(row, reviewer.trim(), note.trim());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="ls-review-modal">
      <div className="ae-card w-full max-w-md p-5 space-y-4 border-emerald-500/30">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400 inline-flex items-center gap-1.5">
              <CheckCircle2 className="h-3 w-3" /> Record review
            </div>
            <h3 className="font-heading text-xl mt-1">
              {row.state_name} <span className="text-slate-500 font-mono text-sm">· {row.state}</span>
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {row.system === "contact_hours" ? "Contact-Hour Rules" : "State Requirements (Document Library)"}
            </p>
          </div>
          <button onClick={onClose} data-testid="ls-review-cancel" className="text-slate-500 hover:text-slate-200">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="text-[11px] text-slate-400 bg-amber-500/5 border border-amber-500/25 rounded-sm p-2.5">
          Recording a review stamps this state as reviewed <span className="text-amber-300">now</span>,
          clearing its staleness flag. This does not change any rule content and is permanently audit-logged.
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">Reviewer name *</label>
          <input
            value={reviewer}
            onChange={(e) => setReviewer(e.target.value)}
            data-testid="ls-review-reviewer"
            placeholder="e.g. Jane Doe, Esq."
            className="w-full px-3 py-1.5 text-sm bg-[#0A0D12] border border-white/10 rounded-sm focus:border-emerald-500/60 outline-none"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">Review note * (min 5 chars)</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid="ls-review-note"
            rows={3}
            placeholder="What was reviewed / confirmed and against which source."
            className="w-full px-3 py-1.5 text-sm bg-[#0A0D12] border border-white/10 rounded-sm focus:border-emerald-500/60 outline-none resize-none"
          />
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} data-testid="ls-review-cancel-btn" className="btn-ghost text-xs">Cancel</button>
          <button
            onClick={submit}
            disabled={!valid || submitting}
            data-testid="ls-review-confirm"
            className="text-xs px-3 py-1.5 rounded-sm bg-emerald-500/20 border border-emerald-500/50 text-emerald-200 disabled:opacity-40 inline-flex items-center gap-1.5"
          >
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
            Record review
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminLegalSourcing() {
  const [data, setData] = useState(null);
  const [decisions, setDecisions] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reseeding, setReseeding] = useState(false);
  const [researching, setResearching] = useState(false);
  const [reviewRow, setReviewRow] = useState(null);
  const [proposalReview, setProposalReview] = useState(null); // {proposal, mode: 'approve'|'reject'}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        api.get("/admin/legal-sourcing"),
        api.get("/admin/legal-sourcing/decisions?limit=200"),
        api.get("/admin/legal-sourcing/proposals"),
      ]);
      setData(a.data);
      setDecisions(b.data);
      setProposals(c.data?.proposals || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load legal sourcing");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reseed = async () => {
    setReseeding(true);
    try {
      await api.post("/admin/legal-sourcing/reseed");
      toast.success("Sourcing rows re-derived");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reseed failed");
    } finally {
      setReseeding(false);
    }
  };

  const runResearch = async () => {
    setResearching(true);
    try {
      const r = await api.post("/admin/legal-sourcing/run-research");
      const s = r.data?.summary || {};
      toast.success(`Research complete: ${s.changed || 0} change(s), ${s.failed || 0} check-fail, ${s.unchanged || 0} unchanged`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Research run failed");
    } finally {
      setResearching(false);
    }
  };

  const submitProposal = async (proposal, mode, reviewer, note, newCitationText, newCitationUrl, attorneyAttested) => {
    try {
      const body = mode === "approve"
        ? { reviewer_name: reviewer, note, new_citation_text: newCitationText || null, new_citation_url: newCitationUrl || null, attorney_attested: !!attorneyAttested }
        : { reviewer_name: reviewer, note };
      await api.post(`/admin/legal-sourcing/proposals/${proposal.pid}/${mode}`, body);
      toast.success(
        mode === "approve"
          ? (attorneyAttested ? `${proposal.state} update approved + marked reviewed` : `${proposal.state} source/citation updated (not reviewed)`)
          : `${proposal.state} update rejected`
      );
      setProposalReview(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to ${mode} proposal`);
    }
  };

  const submitReview = async (row, reviewer, note) => {
    try {
      await api.post("/admin/legal-sourcing/clear-staleness", {
        system: row.system,
        state: row.state,
        content_type: row.content_type,
        reviewer_name: reviewer,
        note,
      });
      toast.success(`${row.state} marked reviewed`);
      setReviewRow(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to record review");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="admin-legal-sourcing-loading">
        <Loader2 className="h-6 w-6 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-legal-sourcing-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2 inline-flex items-center gap-1.5">
            <ShieldAlert className="h-3 w-3" /> Admin · Legal Reference
          </div>
          <h1 className="font-heading text-4xl tracking-tight">Legal Content Sourcing</h1>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            Per-state sourcing for the contact-hour rules and the State-Requirements (Document Library)
            content. Verified primary citations link out to the source; record a
            review on any row to clear its staleness flag. This view never changes substantive rules.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={runResearch} disabled={researching} data-testid="ls-run-research-btn" className="text-xs px-3 py-1.5 rounded-sm bg-sky-500/15 border border-sky-500/40 text-sky-200 hover:bg-sky-500/25 disabled:opacity-40 inline-flex items-center gap-1.5">
            <Radar className={`h-3.5 w-3.5 ${researching ? "animate-spin" : ""}`} /> {researching ? "Researching…" : "Run research now"}
          </button>
          <button onClick={reseed} disabled={reseeding} data-testid="ls-reseed-btn" className="btn-ghost text-xs inline-flex items-center gap-1.5">
            <RefreshCw className={`h-3.5 w-3.5 ${reseeding ? "animate-spin" : ""}`} /> Re-derive
          </button>
        </div>
      </header>

      <div className="ae-card p-4 border-sky-500/30 bg-sky-500/5 flex items-start gap-3" data-testid="ls-research-banner">
        <Radar className="h-4 w-4 text-sky-400 mt-0.5" />
        <div className="text-xs text-slate-300 flex-1">
          <span className="font-semibold text-sky-300">Auto source-research:</span> every{" "}
          {Math.round((data?.research_interval_days || 182) / 30)} months the verified source URLs are
          re-fetched and compared to the last approved snapshot. Changes are never applied
          automatically ,  they surface below as <span className="text-amber-300">Pending source updates</span> for
          review &amp; approval. Sources that can't be fetched are flagged{" "}
          <span className="font-mono text-rose-300">Check failed</span> for manual review.
          <span className="text-slate-500 ml-1">
            Last run: {data?.last_research_run?.ran_at ? fmtDateTime(data.last_research_run.ran_at) : "never (runs on the next scheduled cycle)"}
            {data?.last_research_run?.trigger ? ` · ${data.last_research_run.trigger}` : ""}.
          </span>
        </div>
      </div>

      <ProposalsPanel proposals={proposals} onReview={(p, mode) => setProposalReview({ proposal: p, mode })} />

      <div className="ae-card p-4 border-amber-500/30 bg-amber-500/5 flex items-start gap-3" data-testid="ls-staleness-banner">
        <Clock className="h-4 w-4 text-amber-400 mt-0.5" />
        <div className="text-xs text-slate-300">
          <span className="font-semibold text-amber-300">Staleness policy:</span> every state must be
          reconfirmed by an actual review at least once every {data?.review_interval_days || 365} days.
          States with no recorded review flag <span className="font-mono text-amber-300">Never reviewed</span>;
          use <span className="text-emerald-300">Mark reviewed</span> to record one. This flag never auto-updates rule content.
        </div>
      </div>

      <div className="space-y-4">
        <SourcingTable title="Contact-Hour Rules" icon={Gavel} data={data?.contact_hours} testid="ls-ch-table" system="contact_hours" onReview={setReviewRow} />
        <SourcingTable title="State Requirements (Document Library)" icon={ScrollText} data={data?.document_library} testid="ls-dl-table" system="document_library" onReview={setReviewRow} />
      </div>

      <div className="ae-card p-4" data-testid="ls-decisions-table">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 inline-flex items-center gap-1.5">
            <ScrollText className="h-3 w-3" /> Contact-Hour Block-Decision Log
          </div>
          <div className="text-[10px] text-slate-500 font-mono">{decisions?.total || 0} total decisions</div>
        </div>
        <div className="overflow-auto max-h-[420px] border border-white/8 rounded-sm">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[#0A0D12]">
              <tr className="text-[9px] uppercase tracking-widest text-slate-500 border-b border-white/8">
                <th className="text-left px-3 py-2">Timestamp</th>
                <th className="text-left px-3 py-2">Subscriber</th>
                <th className="text-left px-3 py-2">Opportunity</th>
                <th className="text-left px-3 py-2">State</th>
                <th className="text-left px-3 py-2">Rule fired</th>
              </tr>
            </thead>
            <tbody>
              {(decisions?.decisions || []).length === 0 ? (
                <tr><td colSpan={5} className="text-center py-8 text-slate-500" data-testid="ls-decisions-empty">
                  No block decisions recorded yet.
                </td></tr>
              ) : decisions.decisions.map((d) => (
                <tr key={d.did} className="border-b border-white/5" data-testid={`ls-decision-${d.did}`}>
                  <td className="px-3 py-2 font-mono text-[10px] text-slate-400 whitespace-nowrap">{fmtDateTime(d.created_at)}</td>
                  <td className="px-3 py-2 text-slate-300 text-[10px]">{d.subscriber_email || d.subscriber_uid || ", "}</td>
                  <td className="px-3 py-2 text-slate-300 text-[10px] max-w-[220px] truncate" title={d.oid}>
                    {d.opportunity_title || d.oid || ", "}
                  </td>
                  <td className="px-3 py-2 font-mono text-slate-200">{d.state || ", "}</td>
                  <td className="px-3 py-2 text-slate-400 text-[10px]">{d.rule_fired || ", "}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ReviewModal row={reviewRow} onClose={() => setReviewRow(null)} onSubmit={submitReview} />
      <ProposalReviewModal
        state={proposalReview}
        onClose={() => setProposalReview(null)}
        onSubmit={submitProposal}
      />
    </div>
  );
}


function ProposalsPanel({ proposals, onReview }) {
  return (
    <div className="ae-card p-4 border-amber-500/25" data-testid="ls-proposals-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-[10px] uppercase tracking-widest text-amber-300 inline-flex items-center gap-1.5">
          <BellRing className="h-3 w-3" /> Pending Source Updates
        </div>
        <div className="text-[10px] text-slate-500 font-mono" data-testid="ls-proposals-count">
          {proposals.length} awaiting review
        </div>
      </div>
      {proposals.length === 0 ? (
        <div className="text-center py-8 text-slate-500 text-sm" data-testid="ls-proposals-empty">
          No pending source changes. The next research cycle will surface any updates here for approval.
        </div>
      ) : (
        <div className="space-y-3">
          {proposals.map((p) => (
            <div key={p.pid} className="border border-amber-500/25 bg-amber-500/5 rounded-sm p-3" data-testid={`ls-proposal-${p.pid}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-sm text-slate-100 font-heading">
                    {p.state_name} <span className="font-mono text-slate-500 text-xs">· {p.state}</span>
                    <span className="ml-2 text-[10px] font-mono text-slate-400">
                      {p.system === "contact_hours" ? "Contact-Hour Rules" : "State Requirements"}
                    </span>
                  </div>
                  <a href={p.source_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-sky-400 hover:text-sky-300 inline-flex items-center gap-0.5 mt-0.5">
                    {p.source_url} <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                  <div className="text-[9px] text-slate-500 mt-0.5">Detected {fmtDateTime(p.detected_at)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => onReview(p, "approve")} data-testid={`ls-proposal-approve-${p.pid}`} className="text-[11px] px-2.5 py-1 rounded-sm bg-emerald-500/20 border border-emerald-500/50 text-emerald-200 hover:bg-emerald-500/30 inline-flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" /> Approve &amp; replace
                  </button>
                  <button onClick={() => onReview(p, "reject")} data-testid={`ls-proposal-reject-${p.pid}`} className="text-[11px] px-2.5 py-1 rounded-sm border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 inline-flex items-center gap-1">
                    <Ban className="h-3 w-3" /> Reject
                  </button>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3 mt-3">
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-slate-500 mb-1 inline-flex items-center gap-1"><GitCompareArrows className="h-3 w-3" /> Current snapshot</div>
                  <div className="text-[10px] text-slate-400 bg-black/30 border border-white/8 rounded-sm p-2 max-h-32 overflow-auto font-mono">
                    {p.old_snippet || <span className="italic text-slate-600">,  (baseline empty)</span>}
                  </div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-emerald-400 mb-1 inline-flex items-center gap-1"><GitCompareArrows className="h-3 w-3" /> New source content</div>
                  <div className="text-[10px] text-emerald-100/80 bg-emerald-500/5 border border-emerald-500/20 rounded-sm p-2 max-h-32 overflow-auto font-mono" data-testid={`ls-proposal-newsnippet-${p.pid}`}>
                    {p.new_snippet}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function ProposalReviewModal({ state, onClose, onSubmit }) {
  const [reviewer, setReviewer] = useState("");
  const [note, setNote] = useState("");
  const [newText, setNewText] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [attested, setAttested] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (state) {
      setReviewer("");
      setNote("");
      setAttested(false);
      setNewText(state.proposal?.old_citation_text || "");
      setNewUrl(state.proposal?.official_citation_url || state.proposal?.source_url || "");
    }
  }, [state]);

  if (!state) return null;
  const { proposal, mode } = state;
  const isApprove = mode === "approve";
  const valid = reviewer.trim().length > 0 && note.trim().length >= 5;

  const submit = async () => {
    if (!valid) return;
    setSubmitting(true);
    try {
      await onSubmit(proposal, mode, reviewer.trim(), note.trim(), newText.trim(), newUrl.trim(), attested);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="ls-proposal-modal">
      <div className={`ae-card w-full max-w-lg p-5 space-y-4 max-h-[92vh] overflow-auto ${isApprove ? "border-emerald-500/30" : "border-rose-500/30"}`}>
        <div className="flex items-start justify-between">
          <div>
            <div className={`text-[10px] uppercase tracking-widest inline-flex items-center gap-1.5 ${isApprove ? "text-emerald-400" : "text-rose-400"}`}>
              {isApprove ? <CheckCircle2 className="h-3 w-3" /> : <Ban className="h-3 w-3" />}
              {isApprove ? "Approve source change" : "Reject source update"}
            </div>
            <h3 className="font-heading text-xl mt-1">
              {proposal.state_name} <span className="text-slate-500 font-mono text-sm">· {proposal.state}</span>
            </h3>
          </div>
          <button onClick={onClose} data-testid="ls-proposal-cancel" className="text-slate-500 hover:text-slate-200"><X className="h-4 w-4" /></button>
        </div>

        <div className={`text-[11px] rounded-sm p-2.5 border ${isApprove ? "text-slate-400 bg-emerald-500/5 border-emerald-500/25" : "text-slate-400 bg-rose-500/5 border-rose-500/25"}`}>
          {isApprove
            ? "Approving accepts the new source snapshot as the tracked baseline and applies the citation edits below. By itself this only records that the page changed ,  it does NOT count as a legal-accuracy review unless you attest below."
            : "Rejecting keeps the current citation unchanged. The source will be re-checked on the next cycle and may resurface. Audit-logged."}
        </div>

        {isApprove && (
          <>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">Replacement citation text</label>
              <textarea value={newText} onChange={(e) => setNewText(e.target.value)} data-testid="ls-proposal-new-text" rows={2}
                className="w-full px-3 py-1.5 text-sm bg-[#0A0D12] border border-white/10 rounded-sm focus:border-emerald-500/60 outline-none resize-none font-mono text-[11px]" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">Replacement source URL</label>
              <input value={newUrl} onChange={(e) => setNewUrl(e.target.value)} data-testid="ls-proposal-new-url"
                className="w-full px-3 py-1.5 text-sm bg-[#0A0D12] border border-white/10 rounded-sm focus:border-emerald-500/60 outline-none font-mono text-[11px]" />
            </div>
          </>
        )}

        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">Reviewer name *</label>
          <input value={reviewer} onChange={(e) => setReviewer(e.target.value)} data-testid="ls-proposal-reviewer" placeholder="e.g. Jane Doe, Esq."
            className="w-full px-3 py-1.5 text-sm bg-[#0A0D12] border border-white/10 rounded-sm focus:border-emerald-500/60 outline-none" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">Note * (min 5 chars)</label>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="ls-proposal-note" rows={2} placeholder="What changed and your decision rationale."
            className="w-full px-3 py-1.5 text-sm bg-[#0A0D12] border border-white/10 rounded-sm focus:border-emerald-500/60 outline-none resize-none" />
        </div>

        {isApprove && (
          <label className="flex items-start gap-2 text-[11px] text-slate-300 bg-amber-500/5 border border-amber-500/30 rounded-sm p-2.5 cursor-pointer" data-testid="ls-proposal-attest-label">
            <input type="checkbox" checked={attested} onChange={(e) => setAttested(e.target.checked)} data-testid="ls-proposal-attest" className="mt-0.5 accent-emerald-500" />
            <span>
              <span className="font-semibold text-amber-300">Legal-accuracy attestation.</span>{" "}
              I attest that the new content is legally accurate. Checking this records a
              <span className="text-emerald-300"> legal-accuracy review</span> (clears the staleness flag).
              Leave unchecked to only update the tracked source/citation ,  the state will stay flagged
              <span className="font-mono text-amber-300"> Never reviewed</span> until a review is recorded.
            </span>
          </label>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} data-testid="ls-proposal-cancel-btn" className="btn-ghost text-xs">Cancel</button>
          <button onClick={submit} disabled={!valid || submitting} data-testid="ls-proposal-confirm"
            className={`text-xs px-3 py-1.5 rounded-sm disabled:opacity-40 inline-flex items-center gap-1.5 ${isApprove ? "bg-emerald-500/20 border border-emerald-500/50 text-emerald-200" : "bg-rose-500/20 border border-rose-500/50 text-rose-200"}`}>
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (isApprove ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Ban className="h-3.5 w-3.5" />)}
            {isApprove ? (attested ? "Approve + mark reviewed" : "Approve (source only)") : "Reject update"}
          </button>
        </div>
      </div>
    </div>
  );
}
